import { useState, useEffect, useContext } from "react";
import { AuthContext } from "./AuthContext.js";
import abTestingService from "../services/ABTestingService.js";

/**
 * React hook for A/B testing
 * @param {string} experimentKey - The experiment identifier
 * @param {Object} context - Additional context (calendar, etc.)
 * @returns {Object} A/B test result with variant and utility functions
 */
export const useABTesting = (experimentKey, context = {}) => {
  const { user } = useContext(AuthContext);
  const [assignment, setAssignment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      const result = abTestingService.getAssignment(experimentKey, user, context);
      setAssignment(result);
    } catch (error) {
      console.error(`Error getting A/B test assignment for ${experimentKey}:`, error);
      setAssignment({ variant: null, experimentKey, assigned: false, error: true });
    } finally {
      setIsLoading(false);
    }
  }, [experimentKey, user, context.calendar?._id]); // Re-run if calendar changes

  /**
   * Track conversion for this experiment
   */
  const trackConversion = (eventName, additionalData = {}) => {
    if (!user || !assignment?.assigned) return;
    
    abTestingService.trackConversion(
      experimentKey, 
      eventName, 
      user, 
      {
        ...additionalData,
        calendar_id: context.calendar?._id,
        calendar_handle: context.calendar?.handle
      }
    );
  };

  /**
   * Check if user is in specific variant
   */
  const isVariant = (variantName) => {
    return assignment?.assigned && assignment.variant?.name === variantName;
  };

  /**
   * Get variant data
   */
  const getVariant = () => {
    return assignment?.variant || null;
  };

  return {
    assignment,
    variant: assignment?.variant,
    isAssigned: assignment?.assigned || false,
    isLoading,
    experimentKey,
    trackConversion,
    isVariant,
    getVariant,
  };
};



/**
 * Hook for debugging - shows all active experiments for current user
 */
export const useActiveExperiments = () => {
  const { user } = useContext(AuthContext);
  const [experiments, setExperiments] = useState([]);

  useEffect(() => {
    if (!user) return;
    
    try {
      const active = abTestingService.getActiveExperiments(user);
      setExperiments(active);
    } catch (error) {
      console.error('Error getting active experiments:', error);
    }
  }, [user]);

  return experiments;
};

export default useABTesting; 